// WhatsApp Business configuration page — pilote tes workflows depuis WhatsApp
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  MessageCircle, Check, Send, Sparkles, Bell, BarChart3, FileText, Zap, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const EXAMPLES = [
  { icon: Zap, text: '"Envoie la facture à Martin"', reply: "✅ Facture envoyée à Martin — 14:32" },
  { icon: Send, text: '"Relance tous les impayés"', reply: "✅ 6 relances envoyées — €4 280 récupérables" },
  { icon: BarChart3, text: '"Récap de la semaine"', reply: "📊 12 workflows · 8h économisées · €280 ROI" },
];

export default function WhatsAppConfigPage() {
  const [cfg, setCfg] = useState({ phone_number: "", notify_failures: true, notify_weekly_roi: true, notify_monthly_report: true, connected: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [command, setCommand] = useState("");
  const [cmdResult, setCmdResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/config").then((r) => setCfg({ ...cfg, ...r.data })).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!cfg.phone_number.trim()) return toast.error("Numéro WhatsApp requis");
    setSaving(true);
    try {
      const { data } = await api.post("/whatsapp/config", cfg);
      setCfg({ ...cfg, connected: true });
      toast.success(data.mocked ? "WhatsApp connecté (mode démo)" : "WhatsApp connecté !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/whatsapp/send-test");
      toast.success(data?.mocked ? "Message de test envoyé (mode démo)" : "Message de test envoyé !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setTesting(false);
    }
  };

  const runCmd = async () => {
    if (!command.trim()) return;
    setRunning(true);
    setCmdResult(null);
    try {
      const { data } = await api.post("/whatsapp/command", { message: command });
      setCmdResult(data);
    } catch (e) {
      toast.error("Échec de la commande");
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Chargement...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="whatsapp-config-page">
      {/* Header */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-emerald-500 rounded-sm flex items-center justify-center">
            <MessageCircle size={20} className="text-white" strokeWidth={2}/>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 rounded-sm font-mono text-[10px]">FONCTIONNALITÉ SIGNATURE</Badge>
        </div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">Pilotez FlowMind depuis WhatsApp</h1>
        <p className="text-zinc-600 mt-2 max-w-2xl">
          Recevez vos alertes, déclenchez des workflows et consultez votre ROI sans ouvrir l'app — directement depuis votre WhatsApp.
        </p>
      </div>

      {/* Connection card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-sm p-8 mb-8 border ${cfg.connected ? "bg-emerald-50 border-emerald-200" : "bg-white border-zinc-200"}`}
        data-testid="whatsapp-connection-card"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="overline">{cfg.connected ? "Connecté" : "Connecter WhatsApp"}</span>
            <h2 className="font-display text-2xl font-medium tracking-tight mt-2">
              {cfg.connected ? "Votre numéro est lié" : "2 étapes pour activer"}
            </h2>
          </div>
          {cfg.connected && (
            <Badge className="bg-emerald-600 text-white rounded-sm font-mono text-[10px]">
              <Check size={10} className="mr-1"/> ACTIF
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <Label htmlFor="phone">Numéro WhatsApp</Label>
            <Input
              id="phone"
              value={cfg.phone_number}
              onChange={(e) => setCfg({ ...cfg, phone_number: e.target.value })}
              placeholder="+33 6 12 34 56 78"
              className="rounded-sm mt-2"
              data-testid="whatsapp-phone-input"
            />
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 h-10"
            data-testid="whatsapp-save-button"
          >
            {saving ? "..." : cfg.connected ? "Mettre à jour" : "Connecter"}
          </Button>
        </div>

        {cfg.connected && (
          <div className="flex items-center gap-3 mt-5 pt-5 border-t border-emerald-200 flex-wrap">
            <Button onClick={test} disabled={testing} variant="outline" className="rounded-sm" data-testid="whatsapp-test-button">
              <Send size={14} className="mr-2"/> {testing ? "Envoi..." : "Envoyer un test"}
            </Button>
            {cfg.mocked ? (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-sm font-mono" data-testid="whatsapp-mode-badge">
                ⚠️ Mode démo — Twilio non configuré
              </span>
            ) : (
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-sm font-mono inline-flex items-center gap-1.5" data-testid="whatsapp-mode-badge">
                <Check size={11}/> LIVE — envois réels via Twilio{cfg.sender ? ` (${cfg.sender.replace("whatsapp:", "")})` : ""}
              </span>
            )}
          </div>
        )}
        {cfg.connected && (
          <div className="mt-5 pt-5 border-t border-emerald-200">
            <p className="overline mb-2">Webhook entrant Twilio</p>
            <p className="text-xs text-zinc-600 mb-3">Pour recevoir les messages WhatsApp de vos utilisateurs, configurez ce webhook dans votre console Twilio (Messaging → WhatsApp → "When a message comes in") :</p>
            <code className="block bg-zinc-100 border border-zinc-200 rounded-sm p-3 text-xs font-mono text-zinc-700 break-all" data-testid="whatsapp-webhook-url">
              {window.location.origin}/api/whatsapp/inbound
            </code>
          </div>
        )}
      </motion.div>

      {/* Try a command */}
      {cfg.connected && (
        <div className="bg-black text-white rounded-sm p-8 mb-8 grain relative overflow-hidden" data-testid="whatsapp-try-card">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={18}/>
            <h2 className="font-display text-xl font-medium tracking-tight">Essayez une commande</h2>
            <Badge className="bg-emerald-500 text-white rounded-sm font-mono text-[10px] ml-2">CLAUDE 4.5</Badge>
          </div>
          <p className="text-zinc-400 text-sm mb-4">Tapez la commande que vous enverriez sur WhatsApp. Claude l'interprète et déclenche l'action.</p>
          <div className="flex gap-2 mb-3">
            <Input
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              placeholder="Ex: Exécute le workflow tri factures"
              className="rounded-sm bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
              data-testid="whatsapp-command-input"
            />
            <Button onClick={runCmd} disabled={running} className="rounded-sm bg-emerald-500 text-white hover:bg-emerald-600" data-testid="whatsapp-command-run">
              {running ? "..." : "Exécuter"}
            </Button>
          </div>

          {cmdResult && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-zinc-900 border border-zinc-800 rounded-sm p-4 space-y-2"
              data-testid="whatsapp-command-result"
            >
              <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">Réponse WhatsApp simulée</p>
              <p className="text-sm whitespace-pre-wrap text-emerald-300">{cmdResult.reply_to_user}</p>
              {cmdResult.executed && (
                <p className="text-xs text-zinc-400 flex items-center gap-1.5"><Check size={12} className="text-emerald-400"/> Workflow exécuté · {cmdResult.workflow_name}</p>
              )}
            </motion.div>
          )}
        </div>
      )}

      {/* Examples */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8 mb-8">
        <span className="overline">Exemples de commandes</span>
        <h3 className="font-display text-xl font-medium tracking-tight mt-2 mb-5">Ce que vous pourrez écrire sur WhatsApp</h3>
        <div className="space-y-3">
          {EXAMPLES.map((ex, i) => {
            const Icon = ex.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="border border-zinc-200 rounded-sm overflow-hidden"
              >
                <div className="p-4 bg-zinc-50 border-b border-zinc-200 flex items-center gap-3">
                  <Icon size={14} className="text-zinc-500"/>
                  <span className="text-sm font-mono">{ex.text}</span>
                </div>
                <div className="p-4 bg-emerald-50 flex items-start gap-3">
                  <MessageCircle size={14} className="text-emerald-600 mt-0.5"/>
                  <span className="text-sm">{ex.reply}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Notification preferences */}
      {cfg.connected && (
        <div className="bg-white border border-zinc-200 rounded-sm p-8" data-testid="whatsapp-prefs-card">
          <span className="overline">Préférences</span>
          <h3 className="font-display text-xl font-medium tracking-tight mt-2 mb-5">Quand vous prévenir ?</h3>
          <div className="space-y-4">
            <Toggle icon={AlertCircle} label="Échecs de workflow" desc="Recevez une alerte immédiate dès qu'un workflow échoue." checked={cfg.notify_failures} onChange={(v) => setCfg({ ...cfg, notify_failures: v })} testid="toggle-failures"/>
            <Toggle icon={BarChart3} label="Récap hebdomadaire ROI" desc="Tous les lundis 9h, récap des heures économisées et du ROI." checked={cfg.notify_weekly_roi} onChange={(v) => setCfg({ ...cfg, notify_weekly_roi: v })} testid="toggle-weekly"/>
            <Toggle icon={FileText} label="Rapport mensuel détaillé" desc="Le 1er du mois, bilan complet avec PDF par email." checked={cfg.notify_monthly_report} onChange={(v) => setCfg({ ...cfg, notify_monthly_report: v })} testid="toggle-monthly"/>
          </div>
          <Button onClick={save} disabled={saving} className="mt-6 rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="whatsapp-prefs-save">
            Enregistrer les préférences
          </Button>
        </div>
      )}
    </div>
  );
}

function Toggle({ icon: Icon, label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-zinc-100 last:border-0">
      <div className="flex items-start gap-3">
        <Icon size={16} className="mt-0.5 text-zinc-500 shrink-0"/>
        <div>
          <p className="font-medium text-sm">{label}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid}/>
    </div>
  );
}
